import { CardActions, CardContent, CardHeader, FormHelperText, TextField } from "@mui/material";
import { MprcCard, MprcSpinnerIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { ButtonSend } from "components/buttons/ButtonSend";
import { MprcRequestErrorReport, MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { StandardAccountSendResetPasswordStatus } from "generated/CoreApi_v1";
import { useSnackbar } from 'notistack';
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

type AccountRenewEmailProps = {
    authValue: string;
    onClose: (success: boolean | undefined) => void;
};

export const AccountRenewEmail: React.FC<AccountRenewEmailProps> = ({ authValue, onClose }) => {
    const [email, setEmail] = useState<string | undefined>(undefined);
    const [emailFailed, setEmailFailed] = useState<string[] | undefined>(undefined);
    const [submiting, setSubmiting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();
    const session = useSession();

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);

        const fe = r.fieldsWithError ?? {};
        setEmailFailed(fe["email"]);

        if (r.otherErrors) setViewError(r.otherErrors);

        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    ///
    const handleRenew = () => {
        setSubmiting(true);

        api.v1.account.standardAccountSendResetPassword({ authFieldValue: authValue })
            .then((response) => {
                var result = response.data;

                switch (result.status) {
                    case StandardAccountSendResetPasswordStatus.Done:
                        enqueueSnackbar(t("Auth.AccountRenewEmail.ResendSuccessMessage"), { variant: "success" });
                        onClose(true);
                        break;
                    case StandardAccountSendResetPasswordStatus.EmailNotSend:
                        enqueueSnackbar(t("Auth.AccountRenewEmail.EmailNotSendMessage"), { variant: "error" });
                        break;
                    case StandardAccountSendResetPasswordStatus.NotFound:
                        enqueueSnackbar(t("Auth.Common.AccountNotFoundMessage"), { variant: "error" });
                        break;
                    case StandardAccountSendResetPasswordStatus.InvalidAuth:
                        enqueueSnackbar(t("Auth.Common.InvalidAuthMessageMessage"), { variant: "error" });
                        break;
                }
                setSubmiting(false);
            })
            .catch(e => {
                resolveErrors(e);
                setSubmiting(false);
            });
    };

    return (
        <MprcCard sx={{ width: 450 }} >
            <CardHeader title={t("Auth.AccountRenewEmail.NewEmailTitle")} />
            <CardContent>

                {
                    viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : (
                            <form autoComplete="on" spellCheck={false}>

                                <FormHelperText>{t("Auth.AccountRenewEmail.NewEmailSubtitle")}</FormHelperText>

                                <TextField
                                    id="performaId"
                                    autoFocus
                                    label={session.config.authOptions?.identifierInstructions ?? t("Auth.Login.IdentifierLabel")}
                                    autoComplete="performaId"
                                    margin="normal"
                                    fullWidth={true}
                                    error={!!emailFailed || email?.length == 0}
                                    disabled={submiting}
                                    value={email}
                                    helperText={<MprcStringToHtml text={emailFailed} />}
                                    slotProps={{
                                        input: {
                                            inputProps: { maxLength: 255 }
                                        }
                                    }}
                                    onChange={
                                        (e) => {
                                            setEmail(e.target.value);
                                        }
                                    }
                                />
                            </form>
                        )
                }
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
                <ButtonCancel
                    variant="contained"
                    disabled={submiting}
                    onClick={() => onClose(undefined)}
                />
                {
                    !viewError &&
                    <ButtonSend
                        color="success"
                        variant="contained"
                        disabled={submiting}
                        onClick={handleRenew}
                    >
                        {submiting && <MprcSpinnerIndicator />}
                    </ButtonSend>
                }
            </CardActions>
        </MprcCard>
    );
};
